import React from "react";

interface HowItWorksProps {
  isVisible: boolean;
}

const HowItWorks: React.FC<HowItWorksProps> = ({ isVisible }) => {
  const steps = [
    {
      number: "1",
      title: "Connect Your API",
      description: "Link your OpenAI, ChatGPT, or LLM API account in 60 seconds. We use read-only access to monitor usage and costs securely.",
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      color: "from-indigo-600 to-blue-600",
    },
    {
      number: "2",
      title: "Set Budget Limits",
      description: "Define daily, weekly, or monthly spending limits. Configure custom alerts for different API usage thresholds and cost triggers.",
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: "from-blue-600 to-cyan-600",
    },
    {
      number: "3",
      title: "Get Instant Alerts",
      description: "Receive real-time notifications via email, Slack, or webhook when you approach or exceed your budget. Stay in control of AI spending.",
      icon: (
        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ),
      color: "from-cyan-600 to-teal-600",
    },
  ];

  return (
    <section id="how-it-works" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className={`max-w-6xl mx-auto transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              How Alertaio Works
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Start monitoring your AI API costs in three simple steps. No credit card required.
            </p>
          </div>

          {/* Steps */}
          <div className="relative">
            <div className="grid md:grid-cols-3 gap-24 md:gap-8">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className="relative flex flex-col items-center"
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  {/* Horizontal Connector for Desktop */}
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-12 left-[60%] w-[80%] pointer-events-none">
                      <div className="flex items-center">
                        <div className="flex-1 h-0.5 bg-gradient-to-r from-indigo-300 to-blue-400"></div>
                        <svg className="w-6 h-6 text-blue-500 -ml-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  )}

                  {/* Icon Circle with Number */}
                  <div className={`relative z-10 flex items-center justify-center w-24 h-24 bg-gradient-to-br ${step.color} text-white rounded-full shadow-xl mb-6 hover:scale-110 transition-transform duration-300`}>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md">
                      <span className="text-sm font-bold bg-gradient-to-br from-indigo-600 to-blue-600 bg-clip-text text-transparent">
                        {step.number}
                      </span>
                    </div>
                    {step.icon}
                  </div>

                  {/* Content */}
                  <div className="text-center max-w-xs relative z-10">
                    <h3 className="text-xl font-bold text-slate-900 mb-3">
                      {step.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>

                  {/* Vertical Connector for Mobile - positioned after content */}
                  {index < steps.length - 1 && (
                    <div className="md:hidden flex flex-col items-center mt-8 pointer-events-none">
                      <div className="w-0.5 h-12 bg-gradient-to-b from-indigo-300 to-blue-300"></div>
                      <svg className="w-6 h-6 text-blue-400 -mt-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v10.586l2.293-2.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V4a1 1 0 011-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
